import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { GiftFrequency, InKindGiftInput, PaymentSource } from '@church/domain'
import { useApi } from '../api-context.tsx'

/**
 * Giving, the staff side: funds, campaigns, the ledger, batches, deposits,
 * reports and statements. Every query here needs a `giving:*` grant — the
 * screens that use them are behind one, and the server refuses without it.
 */

export const givingKeys = {
  all: () => ['giving'] as const,
  settings: () => [...givingKeys.all(), 'settings'] as const,
  funds: () => [...givingKeys.all(), 'funds'] as const,
  campaigns: () => [...givingKeys.all(), 'campaigns'] as const,
  pledges: (campaignId: string) => [...givingKeys.all(), 'pledges', campaignId] as const,
  donations: (filters: DonationFilters) => [...givingKeys.all(), 'donations', filters] as const,
  donation: (id: string) => [...givingKeys.all(), 'donation', id] as const,
  donor: (personId: string) => [...givingKeys.all(), 'donor', personId] as const,
  batches: () => [...givingKeys.all(), 'batches'] as const,
  batch: (id: string) => [...givingKeys.all(), 'batch', id] as const,
  deposits: () => [...givingKeys.all(), 'deposits'] as const,
  summary: (year: number) => [...givingKeys.all(), 'summary', year] as const,
  lapsed: (year: number) => [...givingKeys.all(), 'lapsed', year] as const,
  runs: () => [...givingKeys.all(), 'runs'] as const,
  run: (id: string) => [...givingKeys.all(), 'run', id] as const,
  labels: () => [...givingKeys.all(), 'labels'] as const,
  inKind: (filters: InKindFilters) => [...givingKeys.all(), 'in-kind', filters] as const,
  outreach: () => [...givingKeys.all(), 'outreach'] as const,
}

export interface InKindFilters {
  from?: string
  to?: string
}

export interface DonationFilters {
  from?: string
  to?: string
  fundId?: string
  personId?: string
  batchId?: string
  label?: string
}

// ---- settings, funds, campaigns ------------------------------------------------

export function useGivingSettings() {
  const api = useApi()
  return useQuery({
    queryKey: givingKeys.settings(),
    queryFn: async () => {
      const { data, error } = await api.api.v1.giving.settings.get()
      if (error) throw error
      return data
    },
  })
}

export function useUpdateGivingSettings() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: {
      legalName?: string | null
      ein?: string | null
      address?: string | null
    }) => {
      const { error } = await api.api.v1.giving.settings.put(body)
      if (error) throw error
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: givingKeys.settings() }),
  })
}

export function useFunds() {
  const api = useApi()
  return useQuery({
    queryKey: givingKeys.funds(),
    queryFn: async () => {
      const { data, error } = await api.api.v1.giving.funds.get()
      if (error) throw error
      return data
    },
  })
}

export type Fund = NonNullable<ReturnType<typeof useFunds>['data']>[number]

export function useCreateFund() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: { name: string; code?: string | null; isTaxDeductible?: boolean }) => {
      const { data, error } = await api.api.v1.giving.funds.post(body)
      if (error) throw error
      return data
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: givingKeys.funds() }),
  })
}

export function useUpdateFund() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...body }: { id: string; isActive?: boolean; name?: string }) => {
      const { error } = await api.api.v1.giving.funds({ id }).patch(body)
      if (error) throw error
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: givingKeys.funds() }),
  })
}

export function useCampaigns() {
  const api = useApi()
  return useQuery({
    queryKey: givingKeys.campaigns(),
    queryFn: async () => {
      const { data, error } = await api.api.v1.giving.campaigns.get()
      if (error) throw error
      return data
    },
  })
}

export function useCreateCampaign() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: {
      name: string
      fundId: string
      goalCents?: number | null
      startsOn: string
      /** null or absent ⇒ open-ended. */
      endsOn?: string | null
    }) => {
      const { error } = await api.api.v1.giving.campaigns.post(body)
      if (error) throw error
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: givingKeys.campaigns() }),
  })
}

/** A new name, goal or dates; ending a campaign early is moving its end to today. */
export function useUpdateCampaign() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      ...body
    }: {
      id: string
      name?: string
      description?: string | null
      goalCents?: number | null
      startsOn?: string
      endsOn?: string | null
    }) => {
      const { error } = await api.api.v1.giving.campaigns({ id }).patch(body)
      if (error) throw error
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: givingKeys.all() }),
  })
}

export function useCampaignPledges(campaignId: string) {
  const api = useApi()
  return useQuery({
    queryKey: givingKeys.pledges(campaignId),
    enabled: campaignId !== '',
    queryFn: async () => {
      const { data, error } = await api.api.v1.giving.campaigns({ id: campaignId }).pledges.get()
      if (error) throw error
      return data
    },
  })
}

export function useCreatePledge(campaignId: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: {
      personId: string
      jointPersonId?: string | null
      amountCents: number
    }) => {
      const { data, error } = await api.api.v1.giving.pledges.post({ ...body, campaignId })
      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(givingKeys.pledges(campaignId), data)
      void queryClient.invalidateQueries({ queryKey: givingKeys.campaigns() })
    },
  })
}

// ---- the ledger --------------------------------------------------------------

export function useDonations(filters: DonationFilters) {
  const api = useApi()
  return useInfiniteQuery({
    queryKey: givingKeys.donations(filters),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await api.api.v1.giving.donations.get({
        query: {
          limit: 100,
          ...Object.fromEntries(Object.entries(filters).filter(([, value]) => value)),
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      })
      if (error) throw error
      return data
    },
    getNextPageParam: (last) => last.nextCursor,
  })
}

export type Donation = NonNullable<
  ReturnType<typeof useDonations>['data']
>['pages'][number]['items'][number]

export function useDonation(id: string) {
  const api = useApi()
  return useQuery({
    queryKey: givingKeys.donation(id),
    enabled: id !== '',
    queryFn: async () => {
      const { data, error } = await api.api.v1.giving.donations({ id }).get()
      if (error) throw error
      return data
    },
  })
}

/** A refund needs an idempotency key per attempt, so a double click is one refund. */
export function useRefund(donationId: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: { amountCents?: number; reason: string; idempotencyKey: string }) => {
      const { idempotencyKey, ...rest } = body
      const { data, error } = await api.api.v1.giving
        .donations({ id: donationId })
        .refund.post(rest, { headers: { 'idempotency-key': idempotencyKey } })
      if (error) throw error
      return data
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: givingKeys.all() }),
  })
}

/** Labels in use on gifts — the ledger's filter and the label editor's suggestions. */
export function useGivingLabels() {
  const api = useApi()
  return useQuery({
    queryKey: givingKeys.labels(),
    queryFn: async () => {
      const { data, error } = await api.api.v1.giving.labels.get()
      if (error) throw error
      return data
    },
  })
}

/** A gift's labels and staff note: the editable side of a gift that never changes. */
export function useDonationSideData(donationId: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: givingKeys.donation(donationId) })
    void queryClient.invalidateQueries({ queryKey: [...givingKeys.all(), 'donations'] })
    void queryClient.invalidateQueries({ queryKey: givingKeys.labels() })
  }
  const labels = useMutation({
    mutationFn: async (next: string[]) => {
      const { data, error } = await api.api.v1.giving
        .donations({ id: donationId })
        .labels.put({ labels: next })
      if (error) throw error
      return data
    },
    onSuccess: refresh,
  })
  const note = useMutation({
    mutationFn: async (body: string | null) => {
      const { data, error } = await api.api.v1.giving
        .donations({ id: donationId })
        .note.put({ body })
      if (error) throw error
      return data
    },
    onSuccess: refresh,
  })
  return { labels, note }
}

// ---- in-kind gifts -------------------------------------------------------------

export function useInKindGifts(filters: InKindFilters) {
  const api = useApi()
  return useInfiniteQuery({
    queryKey: givingKeys.inKind(filters),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await api.api.v1.giving['in-kind'].get({
        query: {
          limit: 100,
          ...Object.fromEntries(Object.entries(filters).filter(([, value]) => value)),
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      })
      if (error) throw error
      return data
    },
    getNextPageParam: (last) => last.nextCursor,
  })
}

export type InKindGift = NonNullable<
  ReturnType<typeof useInKindGifts>['data']
>['pages'][number]['items'][number]

/** Record, edit or remove a gift of goods or services. */
export function useInKindEditor() {
  const api = useApi()
  const queryClient = useQueryClient()
  const refresh = () => void queryClient.invalidateQueries({ queryKey: givingKeys.all() })
  const create = useMutation({
    mutationFn: async (body: InKindGiftInput) => {
      const { data, error } = await api.api.v1.giving['in-kind'].post(body)
      if (error) throw error
      return data
    },
    onSuccess: refresh,
  })
  const update = useMutation({
    mutationFn: async ({ id, ...body }: Partial<InKindGiftInput> & { id: string }) => {
      const { data, error } = await api.api.v1.giving['in-kind']({ id }).patch(body)
      if (error) throw error
      return data
    },
    onSuccess: refresh,
  })
  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.api.v1.giving['in-kind']({ id }).delete()
      if (error) throw error
    },
    onSuccess: refresh,
  })
  return { create, update, remove }
}

// ---- recurring gifts that run elsewhere ---------------------------------------

export function useRecurringOutreach() {
  const api = useApi()
  return useQuery({
    queryKey: givingKeys.outreach(),
    queryFn: async () => {
      const { data, error } = await api.api.v1.giving.recurring.outreach.get()
      if (error) throw error
      return data
    },
  })
}

export type OutreachRow = NonNullable<ReturnType<typeof useRecurringOutreach>['data']>[number]

export function useMarkContacted() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.api.v1.giving.recurring({ id }).contacted.post()
      if (error) throw error
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: givingKeys.outreach() }),
  })
}

/** "Gives monthly through their bank": a recurring gift we know of but never charge. */
export function useRecordExternalRecurring() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: {
      personId: string
      amountCents: number
      fundId: string
      frequency: GiftFrequency
      startsOn: string
      externalSource?: string | null
      scheduleNote?: string | null
    }) => {
      const { data, error } = await api.api.v1.giving.recurring.external.post(body)
      if (error) throw error
      return data
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: givingKeys.all() }),
  })
}

export function useDonor(personId: string, enabled = true) {
  const api = useApi()
  return useQuery({
    queryKey: givingKeys.donor(personId),
    enabled: enabled && personId !== '',
    queryFn: async () => {
      const { data, error } = await api.api.v1.giving.donors({ id: personId }).get()
      if (error) throw error
      return data
    },
  })
}

export function useSetRecurringStatus() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      status,
    }: {
      id: string
      status: 'active' | 'paused' | 'cancelled'
    }) => {
      const { error } = await api.api.v1.giving.recurring({ id }).patch({ status })
      if (error) throw error
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: givingKeys.all() }),
  })
}

// ---- batches and deposits ------------------------------------------------------

export function useBatches() {
  const api = useApi()
  return useQuery({
    queryKey: givingKeys.batches(),
    queryFn: async () => {
      const { data, error } = await api.api.v1.giving.batches.get()
      if (error) throw error
      return data
    },
  })
}

export function useCreateBatch() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: {
      name: string
      receivedOn: string
      campusId?: string | null
      expectedTotalCents?: number | null
      expectedCount?: number | null
    }) => {
      const { data, error } = await api.api.v1.giving.batches.post(body)
      if (error) throw error
      return data
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: givingKeys.batches() }),
  })
}

/**
 * Batch entry: the open batch, a line at a time, with the running total and
 * the difference from the counting sheet always in view.
 */
export function useBatchEntry(batchId: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  const batch = useQuery({
    queryKey: givingKeys.batch(batchId),
    enabled: batchId !== '',
    queryFn: async () => {
      const { data, error } = await api.api.v1.giving.batches({ id: batchId }).get()
      if (error) throw error
      return data
    },
  })
  const set = (data: unknown) => {
    queryClient.setQueryData(givingKeys.batch(batchId), data)
    void queryClient.invalidateQueries({ queryKey: givingKeys.batches() })
  }
  const add = useMutation({
    mutationFn: async (body: {
      personId?: string | null
      fundId: string
      amountCents: number
      method: 'cash' | 'check' | 'other'
      checkNumber?: string | null
      checkDatedOn?: string | null
      paymentSource?: PaymentSource
      paymentSourceName?: string | null
    }) => {
      const { data, error } = await api.api.v1.giving.batches({ id: batchId }).items.post(body)
      if (error) throw error
      return data
    },
    onSuccess: set,
  })
  const remove = useMutation({
    mutationFn: async (itemId: string) => {
      const { data, error } = await api.api.v1.giving
        .batches({ id: batchId })
        .items({ itemId })
        .delete()
      if (error) throw error
      return data
    },
    onSuccess: set,
  })
  const commit = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.api.v1.giving.batches({ id: batchId }).commit.post()
      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      set(data)
      void queryClient.invalidateQueries({ queryKey: givingKeys.all() })
    },
  })
  return { batch: batch.data, isLoading: batch.isPending, add, remove, commit }
}

export function useDeposits() {
  const api = useApi()
  return useQuery({
    queryKey: givingKeys.deposits(),
    queryFn: async () => {
      const { data, error } = await api.api.v1.giving.deposits.get()
      if (error) throw error
      return data
    },
  })
}

export function useDepositBatches() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: { batchIds: string[]; arrivalOn: string; amountCents: number }) => {
      const { data, error } = await api.api.v1.giving.deposits.post(body)
      if (error) throw error
      return data
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: givingKeys.all() }),
  })
}

/** Test mode only: the processor pays out everything settled. */
export function useTestPayout() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await api.api.v1.giving.test.payout.post()
      if (error) throw error
      return data
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: givingKeys.all() }),
  })
}

// ---- reports and statements -------------------------------------------------------

export function useGivingSummary(year: number) {
  const api = useApi()
  return useQuery({
    queryKey: givingKeys.summary(year),
    queryFn: async () => {
      const { data, error } = await api.api.v1.giving.reports.summary.get({ query: { year } })
      if (error) throw error
      return data
    },
  })
}

export function useLapsedDonors(year: number) {
  const api = useApi()
  return useQuery({
    queryKey: givingKeys.lapsed(year),
    queryFn: async () => {
      const { data, error } = await api.api.v1.giving.reports.lapsed.get({ query: { year } })
      if (error) throw error
      return data
    },
  })
}

export function useStatementRuns() {
  const api = useApi()
  return useQuery({
    queryKey: givingKeys.runs(),
    queryFn: async () => {
      const { data, error } = await api.api.v1.giving.statements.runs.get()
      if (error) throw error
      return data
    },
    // A run is a background job; watch it finish.
    refetchInterval: (query) =>
      query.state.data?.some((run) => run.status === 'queued' || run.status === 'running')
        ? 3_000
        : false,
  })
}

export function useStartStatementRun() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (taxYear: number) => {
      const { data, error } = await api.api.v1.giving.statements.runs.post({ taxYear })
      if (error) throw error
      return data
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: givingKeys.runs() }),
  })
}

export function useRunStatements(runId: string) {
  const api = useApi()
  return useQuery({
    queryKey: givingKeys.run(runId),
    enabled: runId !== '',
    queryFn: async () => {
      const { data, error } = await api.api.v1.giving.statements.runs({ id: runId }).get()
      if (error) throw error
      return data
    },
  })
}
